import { forwardRef, useState } from "react";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { Popover, PopoverPortalContent } from "@/components/_atoms/popover";
import { PopoverTrigger } from "@/components/_atoms/popover";
import { YearPicker } from "@/components/_molecules/datepicker/yearpicker";
import { cn } from "@/lib/cn";

import { DateClearControl } from "./date-clear-control";

export interface InputDateYearSmallProps {
  onValueChange?: (year: number | null) => void;
  className?: string;
  defaultValue?: number;
  placeholder?: string;
  disabled?: boolean;
  minYear?: number;
  maxYear?: number;
  modified?: boolean;
  clearable?: boolean;
  id?: string;
}

export const InputDateYearSmall = forwardRef<HTMLButtonElement, InputDateYearSmallProps>(
  (
    { onValueChange, defaultValue, disabled, placeholder, minYear, maxYear, modified, clearable, className, id },
    ref,
  ) => {
    const { t } = useTranslation("common");
    const [value, setValue] = useState<number | null>(defaultValue ?? null);

    const handleYearSelected = (selectedYear: number | null) => {
      if (selectedYear === null) {
        return;
      }

      setValue(selectedYear);

      if (onValueChange) {
        onValueChange(selectedYear);
      }
    };

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setValue(null);

      if (onValueChange) {
        onValueChange(null);
      }
    };

    const isClearVisible = Boolean(clearable) && value !== null && !disabled;

    return (
      <Popover>
        <span className={cn(["relative inline-flex", className ?? ""])}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              id={id}
              disabled={disabled}
              variant={"ghost"}
              size="xs"
              className={cn(["w-full rounded-md border text-left text-xs font-normal", isClearVisible && "pr-7"])}
              aria-label={t("datepicker.selectYear")}
            >
              <CalendarIcon className="ml-auto mr-1 h-3 w-3 text-muted-foreground" aria-hidden="true" />
              {value !== null ? value : <span className="border text-muted-foreground">{placeholder}</span>}
              {!!modified && <div className="ml-1 h-1.5 w-1.5 rounded-full bg-primary"></div>}
            </Button>
          </PopoverTrigger>
          {isClearVisible && <DateClearControl onClear={handleClear} label={t("datepicker.clearDate")} size="xs" />}
        </span>
        <PopoverPortalContent className="w-auto p-0" align="start">
          <YearPicker
            onValueChange={handleYearSelected}
            defaultValue={value ?? undefined}
            minYear={minYear}
            maxYear={maxYear}
          />
        </PopoverPortalContent>
      </Popover>
    );
  },
);
